import _get from 'lodash/get';
import _every from 'lodash/every';
import _has from 'lodash/has';
import _omit from 'lodash/omit';
import _isEmpty from 'lodash/isEmpty';
import Storage from 'grab-storage';
import {
  STORAGE_SELECTED_COMPANY_ID,
  STORAGE_SELECTED_GROUP_ID,
  STORAGE_USER_COUNTRY_CODE,
  STORAGE_PAX_USE_PERSONAL_PAYMENTS,
  DEFAULT_CITY_MAPPING_COUNTRY_CODE,
} from 'constants/app-constants';
import { isPaxUser } from 'helper/grab-id-auth-helper';
import { enableEnterpriseApiDeprecation } from '../actions/feature-flag-helper';

export const getPrevSelectedCompanyID = () =>
  window.localStorage.getItem(STORAGE_SELECTED_COMPANY_ID);

export const getPrevSelectedGroupID = () =>
  window.localStorage.getItem(STORAGE_SELECTED_GROUP_ID);

export const matchSelectedCompanyAndGroup = companies => {
  const selectedCompanyID = getPrevSelectedCompanyID();
  const selectedGroupID = getPrevSelectedGroupID();
  if (!selectedCompanyID || !selectedGroupID) return {};

  const company = companies.find(c => String(c.id) === selectedCompanyID);
  const group =
    company && company.groups.find(g => String(g.id) === selectedGroupID);
  if (!company || !group) return {};

  return {
    company,
    group,
  };
};

export const saveSelectedCompanyAndGroup = (companyID, groupID) => {
  window.localStorage.setItem(STORAGE_SELECTED_COMPANY_ID, companyID);
  window.localStorage.setItem(STORAGE_SELECTED_GROUP_ID, groupID);
};

// look for first available company with a group from given companies
export const firstValidCompanyAndGroup = companies => {
  const company = companies.find(c => !_isEmpty(_get(c, 'groups[0]')));
  const group = _get(company, 'groups[0]', {});

  if (_isEmpty(company) || _isEmpty(group)) return {};

  return {
    company,
    group,
  };
};

export const getCompany = user => {
  if (user.currentCompany) return user.currentCompany;

  const companies = _get(user, 'companies', []);
  if (_isEmpty(companies)) return {};

  const { company: selectedCompany } = matchSelectedCompanyAndGroup(companies);
  if (!_isEmpty(selectedCompany)) return selectedCompany;

  const { company: firstValidCompany } = firstValidCompanyAndGroup(companies);
  if (!_isEmpty(firstValidCompany)) return firstValidCompany;

  return companies[0];
};

export const getCurrentCompany = user => _get(user, 'currentCompany');

export const getGroup = user => {
  if (user.currentGroup) return user.currentGroup;
  return _get(user, 'companies[0].groups[0]', {});
};

export const getCompanyID = user => getCompany(user).id;

export const getCompanyName = user => getCompany(user).name;

export const getGroupID = user => getGroup(user).id;

export const getGroupName = user => getGroup(user).name;

export const getCompanyCityCode = user =>
  // TODO: remove old flow after rollout
  enableEnterpriseApiDeprecation()
    ? _get(getCompany(user), 'cityCode', '')
    : _get(getCompany(user), 'city.code', '');

export const getCompanyCityName = user => _get(getCompany(user), 'city.name');

const hasRequiredFields = city => {
  const requiredFields = ['code', 'name', 'poi', 'timezone'];
  return _every(requiredFields.map(field => _has(city, field)));
};

export const getDefaultCityForCorpAccount = (user, cities) => {
  const defaultCity = cities.find(
    city =>
      city.code &&
      city.code.toLowerCase() === getCompanyCityCode(user).toLowerCase(),
  );
  return defaultCity && hasRequiredFields(defaultCity)
    ? defaultCity
    : cities.find(city => hasRequiredFields(city)); // still may return undefined
};
export const getDefaultCityForPaxUserAccount = (user, cities) => {
  const countryCode = window.localStorage.getItem(STORAGE_USER_COUNTRY_CODE);
  const defaultCity = cities.find(
    city =>
      city.name && city.name === DEFAULT_CITY_MAPPING_COUNTRY_CODE[countryCode],
  );
  return defaultCity && hasRequiredFields(defaultCity)
    ? defaultCity
    : cities.find(city => hasRequiredFields(city)); // still may return undefined
};
export const getCompanyCountryCode = user => getCompany(user).countryCode;

// works for both gfb and pax users
export const getUserCountryCode = userOrCompany =>
  isPaxUser()
    ? Storage.getItem(STORAGE_USER_COUNTRY_CODE)
    : userOrCompany.countryCode || getCompanyCountryCode(userOrCompany);

export const getUserName = user => _get(user, 'user.name');

export const getUserSafeID = user => _get(user, 'user.safeID');

export const getUserSelectedCityCountryCode = user =>
  _get(user, 'selectedCity.countryCode');

export const getMobileOS = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return { isWinPhone: true };
  }
  if (/android/i.test(userAgent)) {
    return { isAndroid: true };
  }
  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return { isIOS: true };
  }

  return {};
};

// temporarily save user profile in window config, remove after use in global-init-client
export const tempSaveUserProfile = userProfile => {
  window.Grab.config.userProfile = userProfile;
};

export const clearTempUserProfile = () => {
  window.Grab.config = _omit(window.Grab.config, 'userProfile');
};

export const getTempSavedUserProfile = () =>
  _get(window, 'Grab.config.userProfile', {});

export const getTempSavedUserProfileCompanies = () =>
  _get(window, 'Grab.config.userProfile.companies', []);

export const setUsePaxPersonalPayments = isUsing => {
  if (!isPaxUser()) return;

  Storage.setItem(STORAGE_PAX_USE_PERSONAL_PAYMENTS, isUsing);
};

export const isUsingPaxPersonalPayments = () => {
  if (!isPaxUser()) return false;

  const storedValue = Storage.getItem(STORAGE_PAX_USE_PERSONAL_PAYMENTS);
  if (storedValue === 'true') return true;

  if (storedValue === 'false') return false;

  // update default value to true
  setUsePaxPersonalPayments(true);
  return true;
};

// Parse paymentMethods returned from pax business profile to be in same format with GFB user's company payments
export const parsePaxCompany = paxCompany => {
  const payments = _get(paxCompany, 'paymentMethods', []);
  return {
    ...paxCompany,
    paymentMethods: {
      result: Object.keys(payments).map(id => ({
        type: payments[id].type,
        paymentTypeID: id.toString(),
        refNumber: payments[id].cardNumber,
        enabled: true,
      })),
    },
  };
};

export const parsePaxFetchBusinessProfileSuccessAction = action => {
  const response = _get(action, 'data.response', {});
  const companies = _get(response, 'companies', []);
  return {
    ...response,
    companies: companies.map(c => parsePaxCompany(c)),
  };
};

export default {};
